// components/EditAccountModal.jsx
import React, { useState } from "react";
import { X, Loader } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const EditAccountModal = ({ account, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: account.name || "",
    accountNumber: account.accountNumber || "",
    type: account.type || "",
    parentAccountId: account.parentAccountId || "",
    phone: account.phone || "",
    fax: account.fax || "",
    website: account.website || "",
    industry: account.industry || "",
    numberOfEmployees: account.numberOfEmployees || "",
    annualRevenue: account.annualRevenue || "",
    ownership: account.ownership || "",
    rating: account.rating || "",
    sicCode: account.sicCode || "",
    tickerSymbol: account.tickerSymbol || "",

    // Billing Address
    billingStreet: account.billingStreet || "",
    billingCity: account.billingCity || "",
    billingState: account.billingState || "",
    billingPostalCode: account.billingPostalCode || "",
    billingCountry: account.billingCountry || "",

    // Shipping Address
    shippingStreet: account.shippingStreet || "",
    shippingCity: account.shippingCity || "",
    shippingState: account.shippingState || "",
    shippingPostalCode: account.shippingPostalCode || "",
    shippingCountry: account.shippingCountry || "",

    description: account.description || "",
    customerPriority: account.customerPriority || "",
    sla: account.sla || "",
    slaExpirationDate: account.slaExpirationDate
      ? account.slaExpirationDate.split("T")[0]
      : "",
    slaSerialNumber: account.slaSerialNumber || "",
    numberOfLocations: account.numberOfLocations || "",
    upsellOpportunity: account.upsellOpportunity || "",
    active: account.active || "Yes",
    ownerId: account.ownerId || "",
  });

  const [copyBillingToShipping, setCopyBillingToShipping] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCopyAddress = () => {
    if (copyBillingToShipping) {
      setFormData({
        ...formData,
        shippingStreet: formData.billingStreet,
        shippingCity: formData.billingCity,
        shippingState: formData.billingState,
        shippingPostalCode: formData.billingPostalCode,
        shippingCountry: formData.billingCountry,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert("Account name is required");
      return;
    }

    setLoading(true);

    try {
      // Sanitize the data before sending - convert empty strings to null for UUID, enum, numeric, and date fields
      const sanitizedData = {
        ...formData,
        // UUID fields
        parentAccountId: formData.parentAccountId || null,
        ownerId: formData.ownerId || null,
        // Enum fields - convert empty strings to null
        type: formData.type || null,
        industry: formData.industry || null,
        ownership: formData.ownership || null,
        rating: formData.rating || null,
        customerPriority: formData.customerPriority || null,
        sla: formData.sla || null,
        upsellOpportunity: formData.upsellOpportunity || null,
        // Numeric fields - convert empty strings to null
        numberOfEmployees: formData.numberOfEmployees || null,
        annualRevenue: formData.annualRevenue || null,
        numberOfLocations: formData.numberOfLocations || null,
        // Date fields - convert empty strings and invalid dates to null
        slaExpirationDate:
          formData.slaExpirationDate &&
          formData.slaExpirationDate !== "Invalid date"
            ? formData.slaExpirationDate
            : null,
      };

      await apiService.updateAccount(account.id, sanitizedData);
      onSuccess();
    } catch (error) {
      console.error("Error updating account:", error);
      alert(error.response?.data?.message || "Failed to update account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#13161B] border border-gray-700"
            : "bg-white border border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <h2
            className={`text-lg font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Edit Account
          </h2>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} id="editAccountForm">
            {/* Account Information */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Account Information
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="md:col-span-2">
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Account Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                  required
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Account Number
                </label>
                <input
                  type="text"
                  name="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Type
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">--None--</option>
                  <option value="Prospect">Prospect</option>
                  <option value="Customer - Direct">Customer - Direct</option>
                  <option value="Customer - Channel">Customer - Channel</option>
                  <option value="Channel Partner / Reseller">
                    Channel Partner / Reseller
                  </option>
                  <option value="Installation Partner">
                    Installation Partner
                  </option>
                  <option value="Technology Partner">Technology Partner</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Industry
                </label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">--None--</option>
                  <option value="Agriculture">Agriculture</option>
                  <option value="Apparel">Apparel</option>
                  <option value="Banking">Banking</option>
                  <option value="Biotechnology">Biotechnology</option>
                  <option value="Chemicals">Chemicals</option>
                  <option value="Communications">Communications</option>
                  <option value="Construction">Construction</option>
                  <option value="Consulting">Consulting</option>
                  <option value="Education">Education</option>
                  <option value="Electronics">Electronics</option>
                  <option value="Energy">Energy</option>
                  <option value="Engineering">Engineering</option>
                  <option value="Entertainment">Entertainment</option>
                  <option value="Environmental">Environmental</option>
                  <option value="Finance">Finance</option>
                  <option value="Food & Beverage">Food & Beverage</option>
                  <option value="Government">Government</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Hospitality">Hospitality</option>
                  <option value="Insurance">Insurance</option>
                  <option value="Machinery">Machinery</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Media">Media</option>
                  <option value="Not For Profit">Not For Profit</option>
                  <option value="Recreation">Recreation</option>
                  <option value="Retail">Retail</option>
                  <option value="Shipping">Shipping</option>
                  <option value="Technology">Technology</option>
                  <option value="Telecommunications">Telecommunications</option>
                  <option value="Transportation">Transportation</option>
                  <option value="Utilities">Utilities</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Fax
                </label>
                <input
                  type="tel"
                  name="fax"
                  value={formData.fax}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Website
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Annual Revenue
                </label>
                <input
                  type="number"
                  name="annualRevenue"
                  value={formData.annualRevenue}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Number of Employees
                </label>
                <input
                  type="number"
                  name="numberOfEmployees"
                  value={formData.numberOfEmployees}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Ownership
                </label>
                <select
                  name="ownership"
                  value={formData.ownership}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">--None--</option>
                  <option value="Public">Public</option>
                  <option value="Private">Private</option>
                  <option value="Subsidiary">Subsidiary</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Rating
                </label>
                <select
                  name="rating"
                  value={formData.rating}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">--None--</option>
                  <option value="Hot">Hot</option>
                  <option value="Warm">Warm</option>
                  <option value="Cold">Cold</option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  SIC Code
                </label>
                <input
                  type="text"
                  name="sicCode"
                  value={formData.sicCode}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Ticker Symbol
                </label>
                <input
                  type="text"
                  name="tickerSymbol"
                  value={formData.tickerSymbol}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>
            </div>

            {/* Billing Address */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Billing Address
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="md:col-span-2">
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Street
                </label>
                <textarea
                  name="billingStreet"
                  value={formData.billingStreet}
                  onChange={handleChange}
                  rows="2"
                  className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  City
                </label>
                <input
                  type="text"
                  name="billingCity"
                  value={formData.billingCity}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  State/Province
                </label>
                <input
                  type="text"
                  name="billingState"
                  value={formData.billingState}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Postal Code
                </label>
                <input
                  type="text"
                  name="billingPostalCode"
                  value={formData.billingPostalCode}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Country
                </label>
                <input
                  type="text"
                  name="billingCountry"
                  value={formData.billingCountry}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>
            </div>

            {/* Shipping Address */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <div className="flex items-center justify-between">
                <h3
                  className={`text-sm font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Shipping Address
                </h3>
                <label
                  className={`flex items-center gap-2 text-sm cursor-pointer ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={copyBillingToShipping}
                    onChange={(e) => {
                      setCopyBillingToShipping(e.target.checked);
                      if (e.target.checked) {
                        handleCopyAddress();
                      }
                    }}
                    className={`w-4 h-4 rounded border focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "border-gray-700 bg-[#0C0E12] text-blue-600"
                        : "border-gray-300 bg-white text-blue-600"
                    }`}
                  />
                  Copy from Billing
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="md:col-span-2">
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Street
                </label>
                <textarea
                  name="shippingStreet"
                  value={formData.shippingStreet}
                  onChange={handleChange}
                  rows="2"
                  className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  City
                </label>
                <input
                  type="text"
                  name="shippingCity"
                  value={formData.shippingCity}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  State/Province
                </label>
                <input
                  type="text"
                  name="shippingState"
                  value={formData.shippingState}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Postal Code
                </label>
                <input
                  type="text"
                  name="shippingPostalCode"
                  value={formData.shippingPostalCode}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Country
                </label>
                <input
                  type="text"
                  name="shippingCountry"
                  value={formData.shippingCountry}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>
            </div>

            {/* Additional Information */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Additional Information
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Customer Priority
                </label>
                <select
                  name="customerPriority"
                  value={formData.customerPriority}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">--None--</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  SLA
                </label>
                <select
                  name="sla"
                  value={formData.sla}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">--None--</option>
                  <option value="Gold">Gold</option>
                  <option value="Silver">Silver</option>
                  <option value="Platinum">Platinum</option>
                  <option value="Bronze">Bronze</option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  SLA Expiration Date
                </label>
                <input
                  type="date"
                  name="slaExpirationDate"
                  value={formData.slaExpirationDate}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  SLA Serial Number
                </label>
                <input
                  type="text"
                  name="slaSerialNumber"
                  value={formData.slaSerialNumber}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Number of Locations
                </label>
                <input
                  type="number"
                  name="numberOfLocations"
                  value={formData.numberOfLocations}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Upsell Opportunity
                </label>
                <select
                  name="upsellOpportunity"
                  value={formData.upsellOpportunity}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">--None--</option>
                  <option value="Maybe">Maybe</option>
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Active
                </label>
                <select
                  name="active"
                  value={formData.active}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                  placeholder="Enter account description..."
                />
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-3 ${
            isDarkMode
              ? "border-gray-700 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
              isDarkMode
                ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="editAccountForm"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditAccountModal;
